import React from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Target, 
  Trophy,
  Calendar,
  Flame,
  CheckCircle,
  Star,
  Activity
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const recoveryData = [
  { week: 'W1', score: 32 },
  { week: 'W2', score: 38 },
  { week: 'W3', score: 45 },
  { week: 'W4', score: 52 },
  { week: 'W5', score: 58 },
  { week: 'W6', score: 68 },
];

const painData = [
  { week: 'W1', score: 8 },
  { week: 'W2', score: 7 },
  { week: 'W3', score: 6 },
  { week: 'W4', score: 5 },
  { week: 'W5', score: 4 },
  { week: 'W6', score: 3 },
];

const milestones = [
  { title: 'First Session Completed', date: 'Jan 15', achieved: true },
  { title: 'Pain Score Below 5', date: 'Feb 5', achieved: true },
  { title: '50% Recovery', date: 'Feb 12', achieved: true },
  { title: 'Walk 500 Steps', date: 'Feb 20', achieved: true },
  { title: '75% Recovery', date: 'Expected Mar 15', achieved: false },
  { title: 'Full Recovery', date: 'Expected Apr 10', achieved: false },
];

const weeklyStats = [
  { label: 'Exercises Completed', value: 18, total: 21, unit: '' },
  { label: 'Minutes Active', value: 245, total: 300, unit: 'min' },
  { label: 'Sessions Attended', value: 2, total: 2, unit: '' },
  { label: 'Streak', value: 7, total: null, unit: 'days' },
];

export default function PatientProgress() {
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Your Progress</h1>
        <p className="text-sm text-muted-foreground mt-1">Track your recovery journey</p>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {weeklyStats.map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-display font-bold text-foreground">
                    {stat.value}
                  </span>
                  {stat.total && (
                    <span className="text-sm text-muted-foreground">/{stat.total}</span>
                  )}
                  {stat.unit && (
                    <span className="text-sm text-muted-foreground">{stat.unit}</span>
                  )}
                </div>
                {stat.total && (
                  <Progress value={(stat.value / stat.total) * 100} className="h-1.5 mt-2" />
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
      
      {/* Recovery Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-success" />
            Recovery Progress
          </CardTitle>
          <CardDescription>Your improvement over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={recoveryData}>
                <defs>
                  <linearGradient id="progressGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${value}%`, 'Recovery']}
                />
                <ReferenceLine y={90} stroke="hsl(var(--primary))" strokeDasharray="5 5" />
                <Area 
                  type="monotone" 
                  dataKey="score" 
                  stroke="hsl(var(--success))" 
                  strokeWidth={2}
                  fill="url(#progressGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-success" />
              <span className="text-muted-foreground">Your Progress</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-6 h-[2px] bg-primary" style={{ borderStyle: 'dashed' }} />
              <span className="text-muted-foreground">Target (90%)</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pain Score Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-info" />
              Pain Score Trend
            </CardTitle>
            <CardDescription>Lower is better</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={painData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="hsl(var(--info))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--info))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="flex items-center justify-between mt-4 p-3 bg-success-light rounded-lg">
              <span className="text-sm text-success font-medium">Pain reduced by 62.5%!</span>
              <Badge className="bg-success text-success-foreground">Excellent</Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Milestones */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-warning" />
              Milestones
            </CardTitle>
            <CardDescription>Your recovery achievements</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {milestones.map((milestone, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    milestone.achieved ? 'bg-success-light/50' : 'bg-muted/50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    milestone.achieved 
                      ? 'bg-success text-success-foreground' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {milestone.achieved ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Target className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      milestone.achieved ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {milestone.title}
                    </p>
                    <p className="text-xs text-muted-foreground">{milestone.date}</p>
                  </div>
                  {milestone.achieved && (
                    <Star className="w-4 h-4 text-warning" />
                  )}
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Motivational Card */}
      <Card className="bg-accent border-accent">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Flame className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-display font-semibold text-lg text-foreground mb-2">
            Amazing Progress!
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            You've improved 36 points since starting! At this pace, you're on track to reach your recovery goal by April. Keep up the great work!
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
